import React, { useContext, useState, useEffect, useRef } from "react";
import { ProjectContext } from "../../context/project_context";

const ProjectPerLanguage = () => {
  const {
    project,
    org_id,
    project_id,
    memberMode,
    updateProject,
    fetchAvailableTranslations,
    generateTranslations,
    generateTranslationTTS,
    fetchTranslationProgress,
    // Voices
    voices,
    selectedVoiceId,
    setSelectedVoiceId,
    selectedElevenLabsApi,
    handleElevenLabsApiChange,
    // Media playback - for potential future use
    currentPlaybackTime,
    formatTimeCode
  } = useContext(ProjectContext);
  
  const [availableTranslations, setAvailableTranslations] = useState([]);
  const [translationProgress, setTranslationProgress] = useState({});
  const [translationProviders, setTranslationProviders] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState({});

  const getProviderForLanguage = (languageCode) => {
    return translationProviders[languageCode] || 'openai';
  };

  const handleProviderChange = (languageCode, provider) => {
    setTranslationProviders(prev => ({ ...prev, [languageCode]: provider }));
    localStorage.setItem(`translation_provider_${languageCode}`, provider);
  };

  useEffect(() => {
    if (project?.id) {
      loadAvailableTranslations();
    }
  }, [project?.id]);

  useEffect(() => {
    if (availableTranslations.length > 0) {
      // Load progress for all languages
      availableTranslations.forEach(translation => {
        loadProgress(translation.language_code);
      });
    }
  }, [availableTranslations]);

  const loadAvailableTranslations = async () => {
    try {
      const translations = await fetchAvailableTranslations();
      setAvailableTranslations(translations);
      const providers = {};
      translations.forEach((translation) => {
        const stored = localStorage.getItem(`translation_provider_${translation.language_code}`);
        providers[translation.language_code] = stored === 'google' ? 'google' : 'openai';
      });
      setTranslationProviders(providers);
    } catch (error) {
      console.error('Error loading available translations:', error);
    }
  };

  const loadProgress = async (languageCode) => {
    try {
      const progress = await fetchTranslationProgress(languageCode);
      setTranslationProgress(prev => ({
        ...prev,
        [languageCode]: progress
      }));
    } catch (error) {
      console.error(`Error loading progress for ${languageCode}:`, error);
    }
  };

  const handleGenerateTranslations = async (languageCode, options = {}) => {
    setLoading(true);
    setRefreshing(prev => ({ ...prev, [languageCode]: true }));
    try {
      await generateTranslations(languageCode, {
        translation_provider: getProviderForLanguage(languageCode),
        ...options
      });
      // Refresh progress after a short delay
      setTimeout(() => {
        loadProgress(languageCode);
        setRefreshing(prev => ({ ...prev, [languageCode]: false }));
      }, 2000);
    } catch (error) {
      console.error('Error generating translations:', error);
      setRefreshing(prev => ({ ...prev, [languageCode]: false }));
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateTTS = async (languageCode) => {
    setLoading(true);
    setRefreshing(prev => ({ ...prev, [`${languageCode}_tts`]: true }));
    try {
      await generateTranslationTTS(languageCode, selectedVoiceId);
      // Refresh progress after a short delay
      setTimeout(() => {
        loadProgress(languageCode);
        setRefreshing(prev => ({ ...prev, [`${languageCode}_tts`]: false }));
      }, 2000);
    } catch (error) {
      console.error('Error generating TTS:', error);
      alert('Failed to generate translation TTS: ' + error.message);
      setRefreshing(prev => ({ ...prev, [`${languageCode}_tts`]: false }));
    } finally {
      setLoading(false);
    }
  };

  const handleBack = async () => {
    await updateProject({
      ...project,
      project_status: "generate_tts"
    });
  };

  const getProgressPercentage = (progress) => {
    if (!progress || progress.total === 0) return 0;
    return Math.round((progress.completed / progress.total) * 100);
  };

  const refreshProgress = async (languageCode) => {
    await loadProgress(languageCode);
  };

  return (
    <div className="per-language">
      <div className="d-flex justify-content-between align-items-center">
        <h2>Per Language Translation & TTS</h2>
        <div className="d-flex gap-2 align-items-center">
          {/* ElevenLabs API Selector */}
          <select
            className="form-select"
            style={{ width: 'auto', minWidth: '160px' }}
            value={selectedElevenLabsApi}
            onChange={(e) => handleElevenLabsApiChange(e.target.value)}
            title="ElevenLabs TTS model"
          >
            <option value="multilingual_v2">Multilingual v2</option>
            <option value="eleven_v3">Eleven v3</option>
          </select>
          {/* Voice Selector */}
          <select 
            className="form-select" 
            style={{ width: 'auto', minWidth: '180px' }}
            value={selectedVoiceId}
            onChange={(e) => setSelectedVoiceId(e.target.value)}
          >
            <option value="">Default Voice</option>
            {voices.map((voice) => (
              <option key={voice.id} value={voice.voice_id}>
                {voice.name}
              </option>
            ))}
          </select>
          <button 
            className="btn btn-secondary"
            onClick={handleBack}
          >
            Back to Generate TTS
          </button>
        </div>
      </div>

      <div className="mt-3 mb-3">
        <div className="alert alert-info">
          <strong>Note:</strong> Use the download options below each language to get SRT files and audio packages for that specific language.
        </div>
      </div>

      <div className="translations-list mt-4">
        {availableTranslations.length === 0 ? (
          <div className="alert alert-info">
            No available translations found. Please add languages in the AvailableTranslations table.
          </div>
        ) : (
          <div className="table-responsive">
            <table className="table table-bordered">
              <thead>
                <tr>
                  <th>Language</th>
                  <th>Language Code</th>
                  <th>Provider</th>
                  <th>Translation Progress</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {availableTranslations.map((translation) => {
                  const progress = translationProgress[translation.language_code] || { total: 0, completed: 0, pending: 0, in_progress: 0, failed: 0 };
                  const progressPercent = getProgressPercentage(progress);
                  const isRefreshing = refreshing[translation.language_code] || refreshing[`${translation.language_code}_tts`];
                  
                  return (
                    <tr key={translation.id}>
                      <td>{translation.language_name}</td>
                      <td>{translation.language_code}</td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          style={{ minWidth: '160px' }}
                          value={getProviderForLanguage(translation.language_code)}
                          onChange={(e) => handleProviderChange(translation.language_code, e.target.value)}
                          disabled={loading || isRefreshing}
                        >
                          <option value="openai">OpenAI (GPT)</option>
                          <option value="google">Google Translate</option>
                        </select>
                      </td>
                      <td>
                        <div className="progress" style={{ height: '25px' }}>
                          <div 
                            className="progress-bar" 
                            role="progressbar" 
                            style={{ width: `${progressPercent}%` }}
                            aria-valuenow={progressPercent} 
                            aria-valuemin="0" 
                            aria-valuemax="100"
                          >
                            {progressPercent}%
                          </div>
                        </div>
                        <small className="text-muted">
                          {progress.completed} / {progress.total} completed
                          {progress.in_progress > 0 && ` • ${progress.in_progress} in progress`}
                          {progress.failed > 0 && ` • ${progress.failed} failed`}
                        </small>
                      </td>
                      <td>
                        {progress.completed === progress.total && progress.total > 0 ? (
                          <span className="badge bg-success">Completed</span>
                        ) : progress.in_progress > 0 ? (
                          <span className="badge bg-warning">In Progress</span>
                        ) : progress.pending > 0 ? (
                          <span className="badge bg-secondary">Pending</span>
                        ) : (
                          <span className="badge bg-info">Not Started</span>
                        )}
                      </td>
                      <td>
                        <div className="d-flex flex-column gap-2">
                          <div className="btn-group" role="group">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => handleGenerateTranslations(translation.language_code)}
                              disabled={loading || isRefreshing}
                            >
                              {isRefreshing && refreshing[translation.language_code] ? 'Generating...' : 'Generate Translations'}
                            </button>
                            <button
                              className="btn btn-sm btn-outline-warning"
                              onClick={() => {
                                const providerLabel = getProviderForLanguage(translation.language_code) === 'google'
                                  ? 'Google Translate'
                                  : 'OpenAI (GPT)';
                                if (window.confirm(`Re-translate all rows for ${translation.language_name} using ${providerLabel}? Existing translations will be overwritten.`)) {
                                  handleGenerateTranslations(translation.language_code, { force: true });
                                }
                              }}
                              disabled={loading || isRefreshing}
                              title="Re-translate all rows using the selected provider"
                            >
                              Re-translate All
                            </button>
                            <button
                              className="btn btn-sm btn-success"
                              onClick={() => handleGenerateTTS(translation.language_code)}
                              disabled={loading || isRefreshing || progress.completed < progress.total}
                              title={progress.completed < progress.total ? 'Complete translations first' : ''}
                            >
                              {isRefreshing && refreshing[`${translation.language_code}_tts`] ? 'Generating TTS...' : 'Generate TTS'}
                            </button>
                            <button
                              className="btn btn-sm btn-info"
                              onClick={() => {
                                const base = memberMode ? '/orgs' : '/super_admin/orgs';
                                const url = `${base}/${org_id}/projects/${project_id}/translations?language_code=${translation.language_code}`;
                                window.open(url, memberMode ? '_self' : '_blank');
                              }}
                              title="View translations in new window"
                            >
                              View Translations
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => refreshProgress(translation.language_code)}
                              disabled={isRefreshing}
                            >
                              Refresh
                            </button>
                          </div>
                          <div className="btn-group" role="group">
                            <a
                              href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/translation_srt_text?language_code=${translation.language_code}`}
                              className="btn btn-sm btn-outline-primary"
                              target="_blank"
                              download={`${project.name}_${translation.language_code}_translations.srt`}
                              disabled={progress.completed === 0}
                              title={progress.completed === 0 ? 'No translations available' : 'Download SRT file'}
                            >
                              Download SRT
                            </a>
                            <div className="btn-group" role="group">
                              <button
                                className="btn btn-sm btn-outline-success dropdown-toggle"
                                type="button"
                                id={`downloadDropdown_${translation.language_code}`}
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                disabled={progress.completed === 0}
                                title={progress.completed === 0 ? 'No translations available' : 'Download audio package'}
                              >
                                Download Package
                              </button>
                              <ul className="dropdown-menu" aria-labelledby={`downloadDropdown_${translation.language_code}`}>
                                <li>
                                  <a
                                    href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/download_translation_package?language_code=${translation.language_code}&type=individual`}
                                    className="dropdown-item"
                                    target="_blank"
                                    download
                                  >
                                    Individual Audio Files
                                  </a>
                                </li>
                                <li>
                                  <a
                                    href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/download_translation_package?language_code=${translation.language_code}&type=combined`}
                                    className="dropdown-item"
                                    target="_blank"
                                    download
                                  >
                                    Combined Audio File
                                  </a>
                                </li>
                                <li>
                                  <a
                                    href={`/api_super_admin/orgs/${org_id}/projects/${project_id}/download_translation_package?language_code=${translation.language_code}&type=complete`}
                                    className="dropdown-item"
                                    target="_blank"
                                    download
                                  >
                                    Complete Package
                                  </a>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectPerLanguage;

